import axios from 'axios';
import { SvgIcon } from '@progress/kendo-react-common';
import { Avatar, Card, CardBody } from '@progress/kendo-react-layout';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { chevronRightIcon, hyperlinkOpenIcon, infoCircleIcon } from '@progress/kendo-svg-icons';
import { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../../config/Config';
import { EnumType, getIcon, IEnumProps, ProductTypeEnum, valueOf } from '../../../config/Enums';
import { RequestAccessPageResources as AccessRequestResources } from '../../../config/Resources';
import { ConfirmDialog } from '../../layout/panels/ConfirmDialog';
import { IDisplayProps, IMarketDisplayProps } from '../../services/GraphQLShared';
import { Toast, ToastType } from '../../shared/ToastNotification';
import { RequestAccessPageUpdateDialog } from '../view/RequestAccessPageViewPage';

export interface IReportCatalogSummaryProps {
  id: string;
  reportName: string;
  code: string;
  type: string;
  embedArea: string;
  new: boolean;
  rLS: boolean;
  section: IDisplayProps;
  environment: string;
  description?: string;
  market: IMarketDisplayProps;
  formatTags: [IDisplayProps];
  display?: string | undefined;
  status: IEnumProps;
  totalPages: number;
  totalFilters: number;
  modifiedOn: Date;
  modifiedBy: string;
}
export const RenderDashboard = (props) => {
  const isLink = props.item.type == ProductTypeEnum.Link;
  const e = props.item;
  if (e.access) {
    if (isLink && !e.embedded) {
      return (
        <a
          href={e.embedArea}
          onClick={() => axios.post(API_ENDPOINTS.AUDIT_TRAIL_DATA, null, { params: { productId: e.id, productName: e.reportName } })}
          rel="noopener noreferrer"
          target="_blank"
          style={{ textDecoration: 'none' }}
        >
          <ProductDashboardCard {...props} />
        </a>
      );
    }
    return (
      <div onClick={() => props.navigateTo()}>
        <ProductDashboardCard {...props} />
      </div>
    );
  }
  return (
    <div>
      <ProductDashboardCard {...props} />
    </div>
  );
};

const ProductDashboardCard = (props) => {
  const e = props.item;
  const [openRequestAccess, setOpenRequestAccess] = useState(false);
  const [openPendingConfirm, setOpenPendingConfirm] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  // Reflects a just-submitted request immediately, before the (slow) MAM refetch returns.
  const [optimisticPending, setOptimisticPending] = useState(false);
  const onRequestAccess = (item: any) => {
    setSelectedReport(item);
    setOpenRequestAccess(true);
  };
  // A product the user cannot yet access that already has a pending MAM request.
  const requestStatus = String(e.requestStatus ?? '').toLowerCase();
  const isPending = !e.access && e.appInstance && (optimisticPending || requestStatus === 'pending');
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);
  const isLink = e.type == ProductTypeEnum.Link;
  const envChip = valueOf(e.environment, EnumType.EnvironmentType);
  const chipList =
    e.formatTags && e.formatTags.length > 0
      ? e.formatTags.map((x: any) =>
          Object.assign({
            display: x.display,
            id: x.id,
            icon: ` fa fa-${getIcon(x.type, EnumType.TagType)}`,
            color: '',
          }),
        )
      : [];
  chipList.push({ display: envChip.shortText, icon: `fa fa-${envChip.icon}`, id: envChip.code, color: envChip.color });
  return (
    <>
      <Card
        className={`country-cards${e.access ? ' is-clickable' : ''}`}
        style={{
          minWidth: 0,
          minHeight: 112,
          background: 'transparent',
          cursor: e.access ? 'pointer' : 'default',
        }}
      >
        <CardBody className={`app-summary ${!e.access ? (e.appInstance ? 'request-access' : 'blocked') : 'has-access'}`}>
          <Row>
            <Col className="d-flex align-items-center justify-content-between ps-1">
              <div className="d-flex align-items-center">
                {e.market.isoCode !== 'XX' ? (
                  <Avatar rounded="full" type="image" size="large" border={false}>
                    <img
                      src={`/images/icons/${e.id}`}
                      alt={e.market.isoCode}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = `/images/flags/1x1/${e.market.isoCode.toLowerCase()}.svg`;
                      }}
                    />
                  </Avatar>
                ) : (
                  <Avatar rounded="full" type="image" size="large">
                    <img
                      src={`/images/icons/${e.id}`}
                      alt={e.market.isoCode}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = '/images/flags/1x1/global.svg';
                      }}
                    />
                  </Avatar>
                )}
                <Tooltip openDelay={100} position="right">
                  <h4 className="mb-0 report-header" title={e.reportName}>
                    {e.reportName}
                  </h4>
                </Tooltip>
              </div>
              {!e.access && (
                <>
                  {e.appInstance ? (
                    <span
                      className={'badge ' + (isPending ? 'text-bg-warning request-pending' : 'text-bg-primary')}
                      onClick={(evt) => {
                        evt.stopPropagation();
                        if (isPending) {
                          // Pending request already exists: confirm before allowing another (different group) request.
                          setOpenPendingConfirm(true);
                          return;
                        }
                        onRequestAccess(e);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {isPending ? AccessRequestResources.RequestPending : AccessRequestResources.RequestAccess}
                      <SvgIcon icon={chevronRightIcon} className="external-link" />
                    </span>
                  ) : (
                    <span className="badge text-bg-danger" style={{ cursor: 'not-allowed' }}></span>
                  )}
                </>
              )}
              {e.access && (
                <>
                  <span className="badge text-bg-success available">Available</span>
                </>
              )}
              {e.contactOrSupportEmail && (
                <Tooltip openDelay={100} position="right" anchorElement="target" parentTitle={true}>
                  <a
                    href={e.contactOrSupportEmail}
                    title="Click here for more information on this product"
                    onClick={(e) => e.stopPropagation()}
                    rel="noopener noreferrer"
                    target="_blank"
                    style={{ textDecoration: 'none' }}
                  >
                    <SvgIcon icon={infoCircleIcon} className="ms-2 external-link" />
                  </a>
                </Tooltip>
              )}
            </Col>
          </Row>
          <Row>
            <Col md={2}></Col>
            <Col className="ps-1">
              <Tooltip openDelay={100} position="right">
                {e.new && <span className="badge rounded-pill text-bg-success new-state">New</span>}
                <span className="report-desc" title={e.description}>
                  {e.description}
                </span>
              </Tooltip>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              {isLink && !e.embedded && <SvgIcon icon={hyperlinkOpenIcon} className="ms-2 external-link external-link-icon" />}
            </Col>
          </Row>
        </CardBody>
      </Card>
      {selectedReport && (
        <RequestAccessPageUpdateDialog
          visible={openRequestAccess}
          dataItem={selectedReport}
          onClickOk={() => {
            setOpenRequestAccess(false);
            setOptimisticPending(true);
            setToast({
              message: AccessRequestResources.SuccessMessage,
              type: 'success',
            });
          }}
          onClickCancel={() => setOpenRequestAccess(false)}
        />
      )}

      <ConfirmDialog
        title={AccessRequestResources.PendingConfirmTitle}
        message={`${AccessRequestResources.PendingConfirmMessagePrefix}${e.reportName}${AccessRequestResources.PendingConfirmMessageSuffix}`}
        visible={openPendingConfirm}
        onClickOk={() => {
          setOpenPendingConfirm(false);
          onRequestAccess(e);
        }}
        onClickCancel={() => setOpenPendingConfirm(false)}
      />

      <Toast message={toast?.message ?? ''} type={toast?.type} onClose={() => setToast(null)} />
    </>
  );
};
